import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { muxAdminAPI, artistsAPI, adminArtistsAPI } from '../services/api';
import AlertModal from '../components/common/AlertModal';
import ArtistPicker from '../components/AdminDashboard/ArtistPicker';
import BattleDesignationFields from '../components/AdminDashboard/BattleDesignationFields';
import useBattleDesignation from '../components/AdminDashboard/useBattleDesignation';

const EMPTY_BATTLE_FORM = {
  artist_ids: [],
  is_battle: false,
  battle_format: null,
  sides: null,
};

const VideoUpload = () => {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('exclusive');
  const [price, setPrice] = useState('');
  // isFree is retained for internal state tracking but is no longer a separate UI control.
  // Blank price now means free — the UI simply leaves the price field empty.
  const [isFree, setIsFree] = useState(false); // eslint-disable-line no-unused-vars
  const [selectedFile, setSelectedFile] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });

  // Artist selection + battle designation -- reuses the same
  // ArtistPicker/BattleDesignationFields/useBattleDesignation stack as the
  // AdminDashboard VideoModal, so this upload form behaves identically.
  const [artists, setArtists] = useState([]);
  const [battleFormData, setBattleFormData] = useState(EMPTY_BATTLE_FORM);
  const battle = useBattleDesignation(battleFormData, setBattleFormData, artists);

  // Inline "+ New Artist" -- reuses the existing admin artist-create
  // endpoint (POST /admin/artists, same one pages/ArtistManagement.js
  // already uses via adminArtistsAPI.create) rather than building a new
  // create-artist capability.
  const [newArtistName, setNewArtistName] = useState('');
  const [creatingArtist, setCreatingArtist] = useState(false);

  useEffect(() => {
    artistsAPI.getAll()
      .then((res) => setArtists(res.data))
      .catch((error) => console.error('Error fetching artists:', error));
  }, []);

  const handleArtistToggle = (artistId) => {
    const idStr = artistId.toString();
    setBattleFormData((prev) => ({
      ...prev,
      artist_ids: prev.artist_ids.includes(idStr)
        ? prev.artist_ids.filter((id) => id !== idStr)
        : [ ...prev.artist_ids, idStr ],
    }));
  };

  const handleCreateArtist = async () => {
    if (!newArtistName.trim()) return;
    setCreatingArtist(true);
    try {
      const response = await adminArtistsAPI.create(newArtistName.trim());
      setArtists((prev) => [ ...prev, response.data ]);
      setBattleFormData((prev) => ({
        ...prev,
        artist_ids: [ ...prev.artist_ids, response.data.id.toString() ],
      }));
      setNewArtistName('');
    } catch (error) {
      setAlertModal({
        show: true,
        title: 'Create Failed',
        message: error.response?.data?.error || 'Failed to create artist',
        type: 'error',
      });
    } finally {
      setCreatingArtist(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-battle-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  // Builds the roster portion of the create payload: `sides` for a battle,
  // `artist_ids` for a non-battle. Matches the backend contract shared with
  // Api::V1::VideosController#create / Api::V1::Admin::MuxController#create_video.
  const rosterParams = () =>
    battleFormData.is_battle
      ? { sides: battleFormData.sides || {} }
      : { artist_ids: battleFormData.artist_ids };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setAlertModal({ show: true, title: 'Error', message: 'Please select a video file', type: 'error' });
      return;
    }

    if (!title.trim()) {
      setAlertModal({ show: true, title: 'Error', message: 'Please enter a title', type: 'error' });
      return;
    }

    if (battle.battleNeedsAttention) {
      setAlertModal({
        show: true,
        title: 'Error',
        message: 'Finish the battle designation (pick exactly 2 or 4 artists and confirm teams) before uploading.',
        type: 'error',
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get upload URL from Mux via our backend
      const uploadResponse = await muxAdminAPI.createUpload({ title });
      const { upload_url, upload_id } = uploadResponse.data;

      // Step 2: Upload file directly to Mux
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', upload_url);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed: ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(selectedFile);
      });

      // Step 3: Register the video in the database so it appears immediately as processing.
      // Price is now status-independent: send it for any status; blank = free.
      await muxAdminAPI.createVideo({
        title,
        description,
        status,
        upload_id,
        is_battle: battleFormData.is_battle,
        battle_format: battleFormData.battle_format,
        ...rosterParams(),
        // Blank price → backend sets price_cents=nil → free. Backend derives free flag.
        ...(price.trim() ? { price: price.trim() } : {}),
      });

      setAlertModal({
        show: true,
        title: 'Upload Complete',
        message: 'Upload complete! Video is processing and will appear shortly.',
        type: 'success',
      });

      // Reset form
      setTitle('');
      setDescription('');
      setPrice('');
      setIsFree(false);
      setSelectedFile(null);
      setBattleFormData(EMPTY_BATTLE_FORM);
    } catch (error) {
      console.error('Upload error:', error);
      setAlertModal({
        show: true,
        title: 'Upload Failed',
        message: error.response?.data?.error || error.message || 'Failed to upload video',
        type: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-battle-dark py-8">
      <div className="max-w-3xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-white mb-2">Upload Premium Video</h1>
        <p className="text-gray-400 mb-8">Upload battle videos directly to the premium player</p>

        <form onSubmit={handleUpload} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="e.g., MC ALPHA VS MC BETA - AUDIO WARFARE 5"
              disabled={uploading}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Battle description, event details, etc."
              disabled={uploading}
            />
          </div>

          {/* Access Level */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Access Level</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              disabled={uploading}
            >
              <option value="exclusive">Premium (Purchase Required)</option>
              <option value="public">Free (Public)</option>
              <option value="unlisted">Unlisted</option>
            </select>
          </div>

          {/* Price — shown for ALL statuses; leave blank for free (no purchase required) */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Price (USD)
              <span className="text-gray-500 font-normal ml-2">— minimum $0.50 &middot; leave blank for free</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                step="0.01"
                min="0.50"
                placeholder="Leave blank for free"
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-7 pr-4 py-3 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                disabled={uploading}
                data-testid="price-input"
              />
            </div>
            <p className="text-gray-500 text-xs mt-1">
              Any video with a price requires purchase to watch, regardless of its access level.
              Leave blank to make the video free.
            </p>
          </div>

          {/* Artists */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Artists</label>
            <ArtistPicker
              artists={artists}
              selectedIds={battleFormData.artist_ids}
              onToggle={handleArtistToggle}
            />
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={newArtistName}
                onChange={(e) => setNewArtistName(e.target.value)}
                placeholder="New artist name..."
                className="flex-1 bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                disabled={uploading || creatingArtist}
                data-testid="new-artist-name-input"
              />
              <button
                type="button"
                onClick={handleCreateArtist}
                disabled={!newArtistName.trim() || uploading || creatingArtist}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
                data-testid="create-artist-btn"
              >
                {creatingArtist ? 'Adding...' : '+ New Artist'}
              </button>
            </div>
          </div>

          {/* Battle designation */}
          <BattleDesignationFields formData={battleFormData} battle={battle} />

          {/* File Upload */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Video File *</label>
            <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center hover:border-red-500 transition-colors">
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="hidden"
                id="video-file"
                disabled={uploading}
              />
              <label htmlFor="video-file" className="cursor-pointer">
                {selectedFile ? (
                  <div>
                    <p className="text-white font-medium">{selectedFile.name}</p>
                    <p className="text-gray-400 text-sm mt-1">
                      {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-400">Click to select a video file</p>
                    <p className="text-gray-500 text-sm mt-1">MP4, MOV, MKV, or AVI</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div>
              <div className="flex justify-between text-sm text-gray-400 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3">
                <div
                  className="bg-red-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={uploading || !selectedFile || !title.trim() || battle.battleNeedsAttention}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-bold py-4 rounded-lg transition-colors text-lg"
          >
            {uploading ? `Uploading... ${uploadProgress}%` : 'Upload Video'}
          </button>
        </form>

        <AlertModal
          show={alertModal.show}
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
          onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
        />
      </div>
    </div>
  );
};

export default VideoUpload;
